import { useState, useEffect } from 'react';
import { collection, query, where, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { TrendingUp, Package, Tag, DollarSign, Calendar, BarChart3 } from 'lucide-react';

interface PackageStat {
  id: string;
  name: string;
  version: string;
  count: number;
  revenue: number;
}

interface DiscountStat {
  code: string;
  count: number;
  totalDiscount: number;
}

interface DailySale {
  date: string;
  revenue: number;
}

export default function CoursedeeAnalytics() {
  const [loading, setLoading] = useState(true);
  const [revenueToday, setRevenueToday] = useState(0);
  const [revenueThisMonth, setRevenueThisMonth] = useState(0);
  const [revenueTotal, setRevenueTotal] = useState(0);
  const [topPackages, setTopPackages] = useState<PackageStat[]>([]);
  const [topDiscounts, setTopDiscounts] = useState<DiscountStat[]>([]);
  const [dailySales, setDailySales] = useState<DailySale[]>([]);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      setLoading(true);

      // Query all coursedee orders
      const ordersRef = collection(db, 'subscription_payments');
      const q = query(
        ordersRef,
        where('source', '==', 'coursedee_cart'),
        where('status', '==', 'verified')
      );

      const snapshot = await getDocs(q);
      const orders = snapshot.docs.map(doc => doc.data());

      // Calculate revenue
      const now = new Date();
      const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

      let today = 0;
      let thisMonth = 0;
      let total = 0;

      const packageMap = new Map<string, PackageStat>();
      const discountMap = new Map<string, DiscountStat>();
      const dailyMap = new Map<string, number>();

      orders.forEach(order => {
        const amount = order.amount || 0;
        const originalAmount = order.originalAmount || 0;
        const createdAt = order.createdAt?.toDate();

        // Revenue calculations
        total += amount;

        if (createdAt) {
          if (createdAt >= todayStart) {
            today += amount;
          }
          if (createdAt >= monthStart) {
            thisMonth += amount;
          }

          // Daily sales for last 7 days
          const dateKey = createdAt.toLocaleDateString('th-TH', {
            month: 'short',
            day: 'numeric'
          });
          dailyMap.set(dateKey, (dailyMap.get(dateKey) || 0) + amount);
        }

        // Package statistics
        if (order.items && Array.isArray(order.items)) {
          order.items.forEach((item: any) => {
            const key = `${item.id}_${item.version}`;
            const existing = packageMap.get(key);
            
            if (existing) {
              existing.count++;
              existing.revenue += item.finalPrice || item.price || 0;
            } else {
              packageMap.set(key, {
                id: item.id,
                name: item.name,
                version: item.version,
                count: 1,
                revenue: item.finalPrice || item.price || 0
              });
            }
          });
        }

        // Discount statistics
        if (order.discountCode) {
          const code = order.discountCode;
          const discount = originalAmount - amount;
          const existing = discountMap.get(code);

          if (existing) {
            existing.count++;
            existing.totalDiscount += discount;
          } else {
            discountMap.set(code, {
              code,
              count: 1,
              totalDiscount: discount
            });
          }
        }
      });

      setRevenueToday(today);
      setRevenueThisMonth(thisMonth);
      setRevenueTotal(total);

      // Sort and get top packages
      const sortedPackages = Array.from(packageMap.values())
        .sort((a, b) => b.revenue - a.revenue)
        .slice(0, 5);
      setTopPackages(sortedPackages);

      // Sort and get top discounts
      const sortedDiscounts = Array.from(discountMap.values())
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);
      setTopDiscounts(sortedDiscounts);

      // Get last 7 days sales
      const last7Days: DailySale[] = [];
      for (let i = 6; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        const dateKey = date.toLocaleDateString('th-TH', {
          month: 'short',
          day: 'numeric'
        });
        last7Days.push({
          date: dateKey,
          revenue: dailyMap.get(dateKey) || 0
        });
      }
      setDailySales(last7Days);

    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB',
      minimumFractionDigits: 0
    }).format(price);
  };

  const maxRevenue = Math.max(...dailySales.map(d => d.revenue), 1);

  if (loading) {
    return (
      <div className="bg-white rounded-2xl p-8 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">กำลังโหลดข้อมูล...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Revenue Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-blue-500 to-cyan-600 rounded-2xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <p className="text-blue-100 text-sm font-medium">รายได้วันนี้</p>
            <Calendar className="w-5 h-5 text-blue-200" />
          </div>
          <p className="text-3xl font-bold">{formatPrice(revenueToday)}</p>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <p className="text-purple-100 text-sm font-medium">รายได้เดือนนี้</p>
            <TrendingUp className="w-5 h-5 text-purple-200" />
          </div>
          <p className="text-3xl font-bold">{formatPrice(revenueThisMonth)}</p>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <p className="text-green-100 text-sm font-medium">รายได้ทั้งหมด</p>
            <DollarSign className="w-5 h-5 text-green-200" />
          </div>
          <p className="text-3xl font-bold">{formatPrice(revenueTotal)}</p>
        </div>
      </div>

      {/* Sales Chart - Last 7 Days */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
        <div className="flex items-center gap-2 mb-6">
          <BarChart3 className="w-5 h-5 text-purple-600" />
          <h3 className="text-lg font-bold text-gray-900">ยอดขาย 7 วันล่าสุด</h3>
        </div>

        <div className="space-y-3">
          {dailySales.map((day, index) => (
            <div key={index} className="flex items-center gap-3">
              <div className="w-20 text-sm text-gray-600 font-medium">
                {day.date}
              </div>
              <div className="flex-1 bg-gray-100 rounded-full h-8 overflow-hidden">
                <div
                  className="bg-gradient-to-r from-purple-500 to-indigo-600 h-full rounded-full flex items-center justify-end px-3 transition-all duration-500"
                  style={{ width: `${(day.revenue / maxRevenue) * 100}%` }}
                >
                  {day.revenue > 0 && (
                    <span className="text-white text-sm font-bold">
                      {formatPrice(day.revenue)}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Packages */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center gap-2 mb-4">
            <Package className="w-5 h-5 text-purple-600" />
            <h3 className="text-lg font-bold text-gray-900">แพ็คเก็จขายดี</h3>
          </div>

          {topPackages.length === 0 ? (
            <p className="text-center text-gray-500 py-8">ยังไม่มีข้อมูล</p>
          ) : (
            <div className="space-y-3">
              {topPackages.map((pkg, index) => (
                <div
                  key={`${pkg.id}_${pkg.version}`}
                  className="flex items-center justify-between p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-purple-600 text-white rounded-lg flex items-center justify-center font-bold">
                      {index + 1}
                    </div>
                    <div>
                      <p className="font-bold text-gray-900">{pkg.name}</p>
                      <p className="text-sm text-gray-600">{pkg.version}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-purple-600">{formatPrice(pkg.revenue)}</p>
                    <p className="text-sm text-gray-600">{pkg.count} ครั้ง</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Top Discount Codes */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center gap-2 mb-4">
            <Tag className="w-5 h-5 text-orange-600" />
            <h3 className="text-lg font-bold text-gray-900">โค้ดส่วนลดยอดนิยม</h3>
          </div>

          {topDiscounts.length === 0 ? (
            <p className="text-center text-gray-500 py-8">ยังไม่มีข้อมูล</p>
          ) : (
            <div className="space-y-3">
              {topDiscounts.map((discount, index) => (
                <div
                  key={discount.code}
                  className="flex items-center justify-between p-4 bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-orange-600 text-white rounded-lg flex items-center justify-center font-bold">
                      {index + 1}
                    </div>
                    <div>
                      <p className="font-bold text-gray-900">{discount.code}</p>
                      <p className="text-sm text-gray-600">ใช้ไป {discount.count} ครั้ง</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-orange-600">
                      -{formatPrice(discount.totalDiscount)}
                    </p>
                    <p className="text-xs text-gray-600">ส่วนลดรวม</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
